import React from 'react';

export type SavedKeywordItem = {
  id: string;
  keyword: string;
  service: string;
  source?: string;
  searchVolume?: number | null;
  cpc?: number | null;
  competition?: string | null;
  createdAtMs?: number;
};

type Props = {
  items: SavedKeywordItem[];
  loading: boolean;
  onSelect: (item: SavedKeywordItem) => void;
  onDelete: (item: SavedKeywordItem) => void;
};

const formatNumber = (value?: number | null) =>
  typeof value === 'number' ? value.toLocaleString('ja-JP') : '-';

const formatCurrency = (value?: number | null) =>
  typeof value === 'number' ? `$${value.toFixed(2)}` : '-';

const formatDate = (value?: number) => {
  if (!value) return '';

  return new Intl.DateTimeFormat('ja-JP', {
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
};

const SavedKeywords: React.FC<Props> = ({ items, loading, onSelect, onDelete }) => {
  return (
    <section className="rounded border border-gray-200 bg-white p-5 shadow-sm">
      <div className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">保存キーワード</h2>
          <p className="mt-1 text-sm text-gray-500">
            後で掘りたいキーワードをここに残せます。
          </p>
        </div>
        {loading && <span className="text-xs font-semibold text-gray-400">読み込み中...</span>}
      </div>

      <div className="mt-4">
        {!loading && items.length === 0 ? (
          <div className="rounded border border-dashed border-gray-200 bg-white px-3 py-6 text-center text-sm text-gray-500">
            まだ保存キーワードがありません
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-gray-100">
            <table className="min-w-[760px] w-full text-sm">
              <thead className="bg-slate-50 text-xs text-gray-500">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold">キーワード</th>
                  <th className="px-3 py-3 text-left font-semibold">サービス</th>
                  <th className="px-3 py-3 text-right font-semibold">月間平均</th>
                  <th className="px-3 py-3 text-right font-semibold">CPC</th>
                  <th className="px-3 py-3 text-center font-semibold">競合</th>
                  <th className="px-3 py-3 text-right font-semibold">保存日</th>
                  <th className="px-3 py-3 text-right font-semibold">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((item) => (
                  <tr key={item.id} className="hover:bg-blue-50/70">
                    <td className="px-3 py-3 font-semibold text-gray-900">
                      <button
                        type="button"
                        className="text-left hover:text-blue-700"
                        onClick={() => onSelect(item)}
                      >
                        {item.keyword}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-gray-600">{item.service}</td>
                    <td className="px-3 py-3 text-right font-semibold text-gray-700">
                      {formatNumber(item.searchVolume)}
                    </td>
                    <td className="px-3 py-3 text-right text-gray-600">{formatCurrency(item.cpc)}</td>
                    <td className="px-3 py-3 text-center text-gray-600">{item.competition || '-'}</td>
                    <td className="px-3 py-3 text-right text-gray-500">{formatDate(item.createdAtMs)}</td>
                    <td className="px-3 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => onDelete(item)}
                        className="rounded border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default SavedKeywords;
